<template>
  <highcharts
    class="chart"
    :options="chartOptions"
  />
</template>

<script>
import { mapState } from 'vuex';
import moment from 'moment';

import unitPreferencesMixin from '@shared/mixins/unitPreferencesMixin';
import convertToCelsius from '@shared/lib/converter/convertToCelsius';
import convertToCelsiusResult from '@shared/lib/converter/convertToCelsiusResult';

export default {
  name: 'SensorData',
  mixins: [unitPreferencesMixin],
  props: {
    graphData: Array,
  },
  computed: {
    ...mapState({
      csvGenerated: state => state.customerDetail.csvGenerated,
      dailyStats: state => state.customerDetail.dailyStat,
    }),
    sensorGraphData() {
      const supplyTemp = [];
      const returnTemp = [];
      const atticTemp = [];
      const deltaAvgTemp = [];
      const cycleDate = [];
      let sensorTempMin = null;
      let sensorTempMax = null;

      this.graphData.forEach((c) => {
        const {
          supplyAir,
          returnAir,
          atticAir,
          acMode,
          dailyStat,
          startTime,
        } = c;
        let sTemp = supplyAir?.avgTemp;
        let rTemp = returnAir?.avgTemp;
        if (acMode === 'Heat') {
          sTemp = supplyAir?.maxTemp;
          rTemp = returnAir?.maxTemp;
        }
        if (acMode === 'Cool') {
          sTemp = supplyAir?.minTemp;
          rTemp = returnAir?.minTemp;
        }
        if (!sTemp) {
          supplyTemp.unshift(null);
        } else {
          let temp = sTemp;
          if (this.tempFormat === '°C') {
            temp = convertToCelsius(temp);
          }
          temp = Math.round(temp);
          supplyTemp.unshift(temp);
          if (!sensorTempMin) {
            sensorTempMin = temp;
          } else if (sensorTempMin > temp) {
            sensorTempMin = temp;
          }
          if (!sensorTempMax) {
            sensorTempMax = temp;
          } else if (sensorTempMax < temp) {
            sensorTempMax = temp;
          }
        }
        if (!rTemp) {
          returnTemp.unshift(null);
        } else {
          let temp = rTemp;
          if (this.tempFormat === '°C') {
            temp = convertToCelsius(temp);
          }
          temp = Math.round(temp);
          returnTemp.unshift(temp);
          if (!sensorTempMin) {
            sensorTempMin = temp;
          } else if (sensorTempMin > temp) {
            sensorTempMin = temp;
          }
          if (!sensorTempMax) {
            sensorTempMax = temp;
          } else if (sensorTempMax < temp) {
            sensorTempMax = temp;
          }
        }

        if (atticAir?.avgTemp) {
          let temp = atticAir.avgTemp;
          if (this.tempFormat === '°C') {
            temp = convertToCelsius(temp);
          }
          temp = Math.round(temp);
          atticTemp.unshift(temp);
          if (!sensorTempMin) {
            sensorTempMin = temp;
          } else if (sensorTempMin > temp) {
            sensorTempMin = temp;
          }
          if (!sensorTempMax) {
            sensorTempMax = temp;
          } else if (sensorTempMax < temp) {
            sensorTempMax = temp;
          }
        } else {
          atticTemp.unshift(null);
        }

        if (dailyStat) {
          let temp = null;
          if (acMode === 'Heat') {
            temp = dailyStat.heat;
          }
          if (acMode === 'Cool') {
            temp = dailyStat.cool;
          }
          if (this.tempFormat === '°C') {
            temp = convertToCelsiusResult(temp);
          }
          deltaAvgTemp.unshift(Math.abs(temp));
        } else {
          deltaAvgTemp.unshift(null);
        }

        if (startTime) {
          let date = moment(startTime).format('M/D/YY h:mm:ss a');
          if (this.dateFormat === 'D/M') {
            date = moment(startTime).format('D/M/YY h:mm:ss a');
          }
          cycleDate.unshift(date);
        }
      });

      return {
        supplyTemp,
        returnTemp,
        atticTemp,
        deltaAvgTemp,
        cycleDate,
        sensorTempMax,
        sensorTempMin,
      };
    },
    chartOptions() {
      const {
        tempFormat,
        dateFormat,
      } = this;
      const {
        selectPoint,
        // Don't remove this value it is used to refresh the chart
        // Find a better way to refresh later
        // csvGenerated,
      } = this;
      const {
        supplyTemp,
        returnTemp,
        atticTemp,
        deltaAvgTemp,
        cycleDate,
        sensorTempMax,
        sensorTempMin,
      } = this.sensorGraphData;

      const selectedIndex = [];

      return {
        title: '',
        chart: {
          type: 'line',
          zoomType: 'x',
          height: 500,
          spacingTop: 50,
        },
        credits: {
          enabled: false,
        },
        yAxis: [{
          labels: {
            format: `{value}${tempFormat}`,
          },
          title: {
            text: 'Supply/Return T',
          },
          min: sensorTempMin - 4,
          max: sensorTempMax + 4,
        }, {
          labels: {
            format: `{value}${tempFormat}`,
          },
          title: {
            text: 'Delta T',
          },
          min: Math.min(...deltaAvgTemp) - 4,
          max: Math.max(...deltaAvgTemp) + 4,
          opposite: true,
        }],
        xAxis: [{
          categories: cycleDate,
        }],
        legend: {
          layout: 'vertical',
          align: 'right',
          verticalAlign: 'middle',
        },
        plotOptions: {
          series: {
            label: {
              connectorAllowed: true,
            },
            connectNulls: true,
            marker: {
              states: {
                select: {
                  lineColor: 'rgba(100,100,200, 0.25)',
                  fillColor: 'rgba(223, 83, 83, 1)',
                  lineWidth: 10,
                  radius: 6,
                },
              },
            },
            point: {
              events: {
                click() {
                  let unMarkIndex;
                  if (selectedIndex.length >= 2) {
                    unMarkIndex = selectedIndex.shift();
                  }
                  selectedIndex.push(this.index);
                  this.series.chart.series.forEach((s) => {
                    if (unMarkIndex) {
                      // eslint-disable-next-line
                      s.data[unMarkIndex].selected = false;
                    }
                    s.data[this.index].selected = !s.data[this.index].selected;
                    s.redraw();
                  });
                  selectPoint(this);
                },
              },
            },
          },
        },
        series: [{
          name: 'Supply T',
          color: 'rgba(119, 152, 191, 1)',
          data: supplyTemp,
        }, {
          name: 'Return T',
          color: 'rgba(223, 83, 83, 1)',
          data: returnTemp,
        }, {
          name: 'Air Handler T',
          color: 'rgba(240, 240, 0, 1)',
          data: atticTemp,
        }, {
          name: 'Delta T Average',
          color: 'rgba(0, 255, 0, 1)',
          data: deltaAvgTemp,
          yAxis: 1,
        }],
        responsive: {
          rules: [{
            condition: {
              maxWidth: 2000,
            },
            chartOptions: {
              legend: {
                layout: 'horizontal',
                align: 'center',
                verticalAlign: 'bottom',
              },
            },
          }],
        },
      };
    },
  },
  methods: {
    selectPoint(point) {
      const data = this.graphData[this.graphData.length - point.index];
      this.$store.dispatch('selectDataForExport', {
        data,
      });
    },
  },
};
</script>
